import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, CircularProgress, Icon, Tooltip, Alert, IconButton } from "@mui/material";
import html2canvas from "html2canvas";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";

import SnapshotCaptureDialog from "./SnapshotCaptureDialog";

interface SnapshotCaptureButtonProps {
  targetRef?: React.RefObject<HTMLElement>;
  variant?: "button" | "icon";
  tooltip?: string;
  iconColor?: string;
}

export default function SnapshotCaptureButton({
  targetRef,
  variant = "button",
  tooltip = "Capture a screenshot of the current view",
  iconColor = "inherit",
}: SnapshotCaptureButtonProps) {
  const [capturing, setCapturing] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [imageData, setImageData] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fallbackRef = useRef<HTMLElement | null>(typeof document !== "undefined" ? document.body : null);
  useEffect(() => {
    if (!fallbackRef.current && typeof document !== "undefined") {
      fallbackRef.current = document.body;
    }
  }, []);

  const captureRef = targetRef ?? fallbackRef;
  const isIconVariant = variant === "icon";

  const handleCapture = useCallback(async () => {
    if (!captureRef.current) {
      setError("Unable to capture snapshot – target not ready yet.");
      return;
    }
    setError(null);
    setCapturing(true);
    try {
      const canvas = await html2canvas(captureRef.current, {
        useCORS: true,
        logging: false,
      });
      const dataUrl = canvas.toDataURL("image/png");
      setImageData(dataUrl);
      setDialogOpen(true);
    } catch (err: any) {
      setError(err?.message || "Failed to capture snapshot");
    } finally {
      setCapturing(false);
    }
  }, [captureRef]);

  const handleDialogClose = useCallback(() => {
    setDialogOpen(false);
    setImageData(null);
  }, []);

  const buttonContent = useMemo(() => {
    if (capturing) {
      return <CircularProgress size={18} sx={{ color: "#0b1a24" }} />;
    }
    return (
      <>
        <Icon sx={{ mr: 1 }}>photo_camera</Icon>
        Snapshot
      </>
    );
  }, [capturing]);

  const renderTrigger = () => {
    if (isIconVariant) {
      return (
        <IconButton
          onClick={handleCapture}
          disabled={capturing}
          size="small"
          sx={{
            color: iconColor,
            border: "1px solid rgba(255,255,255,0.3)",
            width: 32,
            height: 32,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {capturing ? (
            <CircularProgress size={14} sx={{ color: iconColor, opacity: 0.85 }} />
          ) : (
            <PhotoCameraOutlinedIcon fontSize="inherit" />
          )}
        </IconButton>
      );
    }
    return (
      <Button
        variant="contained"
        color="secondary"
        onClick={handleCapture}
        disabled={capturing}
        sx={{
          display: "flex",
          alignItems: "center",
          minWidth: 160,
          textTransform: "none",
          fontWeight: 600,
        }}
      >
        {buttonContent}
      </Button>
    );
  };

  return (
    <Box
      sx={{
        display: isIconVariant ? "inline-flex" : "flex",
        flexDirection: isIconVariant ? "row" : "column",
        gap: isIconVariant ? 0 : 1,
        position: isIconVariant ? "relative" : "static",
      }}
    >
      {error && (
        <Alert
          severity="error"
          onClose={() => setError(null)}
          sx={{
            width: 240,
            boxShadow: 2,
            bgcolor: "rgba(255,255,255,0.95)",
            position: isIconVariant ? "absolute" : "relative",
            top: isIconVariant ? "calc(100% + 8px)" : "auto",
            right: isIconVariant ? 0 : "auto",
            zIndex: isIconVariant ? 10 : "auto",
          }}
        >
          {error}
        </Alert>
      )}
      <Tooltip title={tooltip} placement="left" arrow>
        <span>
          {renderTrigger()}
        </span>
      </Tooltip>

      <SnapshotCaptureDialog
        open={dialogOpen}
        imageData={imageData}
        onClose={handleDialogClose}
      />
    </Box>
  );
}
